import styled from "@emotion/styled";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { useEffect, useMemo, useRef, useState } from "react";
import VideoProvider from "@/modules/common/video-player/player.provider";
import { EVENT, send } from "@/lib/amplitude";
import ENV_PUBLIC from "@/scripts/client/ENV_PUBLIC";
import DubbedHeader from "./components/DubbedHeader";
import Popup from "./components/Popup";
import DubbedVideo from "./components/DubbedVideo";
import DubbedSkeleton from "./components/DubbedSkeleton";
import AdSense from "./components/AdSense";

const genId = () => {
  return Math.random().toString(36).substr(2, 16);
};

const listGenerator = (
  idx: number | null,
  item_seq: number,
  viewPopup = false,
  isAd = false,
) => {
  return {
    idx: idx ?? genId(),
    viewPopup,
    isAd,
    item_seq,
  };
};

const DubbedMobile = ({
  firstDubbed,
  firstScript,
  dubbedList,
  locale,
  isIos,
}: any) => {
  const swiperRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [curIdx, setCurIdx] = useState(0);
  const [isFirst, setIsFirst] = useState(true);

  const lists = useMemo(() => {
    if (!dubbedList)
      return [
        listGenerator(firstDubbed.idx, 1),
        listGenerator(null, 0, false, true),
      ];

    const totalList = [
      firstDubbed.idx,
      ...dubbedList.filter((el: number) => el !== firstDubbed.idx),
    ];
    const arr = [];

    for (let i = 0; i < totalList.length; i += 1) {
      const idx = totalList[i];
      if (i === 0) {
        arr.push(listGenerator(idx, i + 1));
      } else {
        const viewPopup = (i + 1) % 5 === 0;
        const addAd = (i + 1) % 3 === 0;
        arr.push(listGenerator(idx, i + 1, viewPopup));
        if (addAd) arr.push(listGenerator(null, 0, false, true));
      }
    }

    return arr;
  }, []);

  const handleInit = (swiper) => {
    swiperRef.current = swiper;
  };

  const handleChange = (swiper) => {
    const dubbed =
      swiper.swipeDirection === "next"
        ? lists[swiper.activeIndex - 1]
        : lists[swiper.activeIndex + 1];
    if (dubbed && !dubbed.isAd) {
      send(EVENT.SWIPE__DUB_VIDEO, {
        dubbed_idx: dubbed.idx,
        swipe_to: swiper.swipeDirection,
        item_seq: dubbed.item_seq,
      });
    }

    setCurIdx(swiper.activeIndex);
  };

  const handleNextSwipe = () => {
    const swiper = swiperRef.current;
    swiper?.slideNext();
  };

  useEffect(() => {
    window.adsbygoogle = window.adsbygoogle || [];
  }, []);

  const currentDubbedIdx = lists[curIdx];

  return (
    <Container>
      <DubbedHeader locale={locale} />
      <Swiper
        ref={swiperRef}
        direction="vertical"
        slidesPerView={1}
        className="mySwiper"
        onInit={handleInit}
        onSlideChange={handleChange}
      >
        <SwiperSlide>
          <FirstDubbedFeed
            dubbed={firstDubbed}
            scriptData={firstScript}
            setShowPopup={setShowPopup}
            locale={locale}
            handleNextSwipe={handleNextSwipe}
            isIos={isIos}
            isFirst={isFirst}
            setIsFirst={setIsFirst}
          />
        </SwiperSlide>

        {lists.slice(1).map((el, i) => (
          <SwiperSlide key={`${el.idx}_${el.item_seq}`}>
            {el.isAd ? (
              <DubbedAd isView={i + 1 === curIdx} />
            ) : (
              <DubbedFeed
                itemSeq={el.item_seq}
                isView={i === curIdx}
                dubbedIdx={el.idx}
                setShowPopup={setShowPopup}
                isPopupView={el.viewPopup}
                locale={locale}
                handleNextSwipe={handleNextSwipe}
                isIos={isIos}
                isFirst={isFirst}
                setIsFirst={setIsFirst}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      <Popup
        dubbedIdx={currentDubbedIdx.idx}
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        locale={locale}
      />
    </Container>
  );
};

const DubbedAd = ({ isView }: { isView: boolean }) => {
  const [isShow, setIsShow] = useState(false);
  useEffect(() => {
    if (isView && !isShow) {
      setIsShow(true);
    }
  }, [isView]);

  if (isShow) {
    return <AdSense />;
  }

  return null;
};

const FirstDubbedFeed = ({
  dubbed,
  scriptData,
  setShowPopup,
  locale,
  handleNextSwipe,
  isFirst,
  setIsFirst,
  isIos,
}: any) => {
  return (
    <VideoProvider video={dubbed.video}>
      <DubbedVideo
        dubbed={{ ...dubbed, scriptData }}
        setShowPopup={setShowPopup}
        isPopupView={false}
        locale={locale}
        handleNextSwipe={handleNextSwipe}
        isFirst={isFirst}
        setIsFirst={setIsFirst}
        isIos={isIos}
        itemSeq={1}
      />
    </VideoProvider>
  );
};

const DubbedFeed = ({
  isView,
  dubbedIdx,
  setShowPopup,
  locale,
  isPopupView,
  handleNextSwipe,
  isFirst,
  setIsFirst,
  isIos,
  itemSeq,
}: any) => {
  const [dubbed, setDubbed] = useState<{
    code: number;
    data: any;
    error?: any;
  } | null>(null);

  useEffect(() => {
    if (isView) {
      if (!dubbed) {
        Promise.all([getDubbed(dubbedIdx), getScript(dubbedIdx)]).then(
          (res) => {
            const [dubbedRes, script] = res;

            if (dubbedRes.code !== 200) {
              setDubbed(dubbedRes);
              return;
            }
            if (script.code !== 200) {
              setDubbed(script);
              return;
            }

            setDubbed({
              code: 200,
              data: {
                ...dubbedRes.data,
                scriptData: script.data,
              },
            });
          },
        );
      }
    }
  }, [isView]);

  if (!dubbed) {
    return <DubbedSkeleton />;
  }

  if (dubbed.code !== 200) {
    return <ErrorMessage>{dubbed.error.errorMessage}</ErrorMessage>;
  }

  if (dubbed) {
    return (
      <VideoProvider video={dubbed.data.video}>
        <DubbedVideo
          itemSeq={itemSeq}
          dubbed={dubbed.data}
          setShowPopup={setShowPopup}
          isPopupView={isPopupView}
          locale={locale}
          handleNextSwipe={handleNextSwipe}
          isFirst={isFirst}
          setIsFirst={setIsFirst}
          isIos={isIos}
        />
      </VideoProvider>
    );
  }

  return null;
};

const getDubbed = async (idx: number) => {
  try {
    const response = await fetch(
      `${ENV_PUBLIC.NEXT_PUBLIC_2DUB_URL}/api/video/dubbed/${idx}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      return {
        code: 500,
        error: {
          errorMessage: response.statusText,
        },
      };
    }

    const result = await response.json();

    return result;
  } catch (error) {
    return {
      code: 500,
      error: {
        errorMessage: error.message,
      },
    };
  }
};

const getScript = async (idx: number) => {
  try {
    const response = await fetch(
      `${ENV_PUBLIC.NEXT_PUBLIC_2DUB_URL}/api/v2/dubbed/${idx}/script`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      return {
        code: 500,
        error: {
          errorMessage: response.statusText,
        },
      };
    }

    const result = await response.json();

    return result;
  } catch (error) {
    return {
      code: 500,
      error: {
        errorMessage: error.message,
      },
    };
  }
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding-top: 44px;
  max-width: 400px;
  margin: 0 auto;
  background: black;
  .swiper {
    height: 100%;
  }

  .swiper-slide {
    background: black;
  }
`;

const ErrorMessage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: black;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 0 32px;

  color: var(--white);
  text-align: center;
  font-size: var(--fs-b1);
  line-height: var(--lh-b1);
`;

export default DubbedMobile;
